import { useId } from 'react'
import FormRow from '../../components/FormRow'
import InfoHint from '../../components/InfoHint'
import TextField from '../../components/TextField'
import { formatFileSize } from '../../lib/fileSize'
import { isEnglish } from '../../lib/i18n'
import { settingsStrings } from './strings'

const H = settingsStrings.hub

/**
 * The few pieces a settings section needs that the shared kit does not have. The controls
 * themselves stay the kit's own — TextField, Select, FileField — so a settings form reads
 * like every other form in the panel; what is the settings' own is the ROW they sit in.
 */

/**
 * A group of fields: a panel of its own on the section's canvas, headed by its name and an
 * icon on a tile of the identity colour. Every field in a section sits in one, so a
 * section reads as a few clear panels rather than one long column.
 *
 * @param {import('react').ElementType} [icon]
 * @param {import('react').ReactNode} [aside]  an action for the whole group, at the far end
 *   of its head
 */
export function Block({ title, icon: Icon, aside, toggle, children }) {
  const titleId = useId()
  /* A panel that IS a feature carries its switch in its head, and while the feature is off
     the panel is only that head — the settings of a channel nobody uses are not something
     to read past. */
  const open = !toggle || toggle.checked
  const body = open && children

  return (
    <section className={`set-block${body ? '' : ' is-folded'}${toggle ? ' has-toggle' : ''}`}>
      {title && (
        <header className="set-block__head">
          {Icon && (
            <span className="set-block__icon" aria-hidden="true">
              <Icon size={16} strokeWidth={2} />
            </span>
          )}
          <h3 className="set-sub" id={titleId}>
            <bdi>{title}</bdi>
          </h3>
          {aside && <span className="set-block__aside">{aside}</span>}
          {toggle && (
            <span className="set-block__aside">
              <Switch checked={toggle.checked} onChange={toggle.onChange} labelledBy={titleId} />
            </span>
          )}
        </header>
      )}
      {body && <div className={`set-block__body${toggle ? ' set-reveal' : ''}`}>{children}</div>}
    </section>
  )
}

/**
 * The kit's switch without the kit's boxed row — for a panel head or a switch row. A
 * `<label>` round the input because the kit's input takes no pointer events: the track is
 * what gets pressed.
 *
 * @param {string} [id]  for a row whose title is the switch's label
 * @param {string} [labelledBy]  for a panel head, whose title is not a label
 */
export function Switch({ id, checked, onChange, labelledBy }) {
  return (
    <label className={`switch-field set-switch${checked ? ' is-on' : ''}`}>
      <input
        id={id}
        type="checkbox"
        role="switch"
        className="switch-field__input"
        checked={checked}
        aria-labelledby={labelledBy}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="switch-field__track" aria-hidden="true">
        <span className="switch-field__thumb" />
      </span>
    </label>
  )
}

/* ======================================================================================
   ONE SETTING, ONE ROW.

   Every setting in a panel is a row: its name on one side, its control on the other, a
   hairline between one setting and the next. No icon per row — the panel's head already
   says what the group is about, and a glyph on every line is decoration the eye has to
   read past. Hierarchy comes from the type and the alignment instead.
   ====================================================================================== */

/**
 * A setting with an input: the kit's FormRow — label wiring, hint, error — laid out as a
 * row. `stack` puts the control under the name instead of beside it, for controls that
 * need the width: long text.
 */
export function Row({ label, hint, stack = false, children }) {
  return (
    <FormRow className={`set-row${stack ? ' set-row--stack' : ''}`} label={label} hint={hint}>
      {children}
    </FormRow>
  )
}

/**
 * A row whose control is not one input — a set of choices. Its name is a heading the
 * control points at, not a label. `children` may take the heading's id.
 */
export function RowGroup({ label, children }) {
  const id = useId()
  return (
    <div className="set-row set-row--stack" role="group" aria-labelledby={id}>
      <span className="set-row__label" id={id}>
        <bdi>{label}</bdi>
      </span>
      <div className="set-row__control">{typeof children === 'function' ? children(id) : children}</div>
    </div>
  )
}

/** A setting that is on or off: the row is the switch's label, the switch at its end. */
export function SwitchRow({ label, hint, checked, onChange }) {
  const id = useId()
  return (
    <div className="set-row set-row--switch">
      <span className="set-row__label">
        <label className="set-row__hit" htmlFor={id}>
          <bdi>{label}</bdi>
        </label>
        {hint && <InfoHint content={<bdi>{hint}</bdi>} />}
      </span>
      <Switch id={id} checked={checked} onChange={onChange} />
    </div>
  )
}

/**
 * A setting kept in both languages: one row, the Arabic field and the English one side by
 * side. Each names its language as its placeholder (2026-10-22) — a filled field says it
 * in its own script — rather than as a tag at its far end, which a long English value ran
 * underneath. The language is part of each field's accessible name either way.
 */
export function Pair({ label, hint, arabic, english, onArabic, onEnglish }) {
  return (
    <Row label={label} hint={hint}>
      {(id) => (
        <div className="set-pair">
          <TextField
            id={id}
            value={arabic}
            onChange={onArabic}
            dir="rtl"
            placeholder={H.inArabic}
            aria-label={`${label} (${H.inArabic})`}
          />
          <TextField
            value={english}
            onChange={onEnglish}
            dir="ltr"
            placeholder={H.inEnglish}
            aria-label={`${label} (${H.inEnglish})`}
          />
        </div>
      )}
    </Row>
  )
}

/** A value shown, not edited: its row, with the value as a token at the far end. */
export function ReadValue({ label, children, mono = false }) {
  return (
    <div className="set-row set-row--read">
      <span className="set-row__label">
        <bdi>{label}</bdi>
      </span>
      <span className={`set-read__value${mono ? ' is-mono' : ''}`} dir={mono ? 'ltr' : undefined}>
        <bdi>{children ?? '—'}</bdi>
      </span>
    </div>
  )
}

/**
 * The same few decisions taken for each of a list of things — which profile fields show
 * and which the user may edit, which columns each report carries. One grid instead of a
 * checkbox row per field with the column's meaning repeated on every line.
 */
export function Matrix({ caption, head, columns, rows, cell, fields = false }) {
  return (
    <div className="set-matrix-wrap">
      <table className={`set-matrix${fields ? ' set-matrix--fields' : ''}`}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            <th scope="col">
              <bdi>{head}</bdi>
            </th>
            {columns.map((column) => (
              <th key={column.id} scope="col">
                <bdi>{column.label}</bdi>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <th scope="row">
                <bdi>{row.label}</bdi>
              </th>
              {columns.map((column) => (
                <td key={column.id}>{cell(row, column)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Options that each need a sentence, as CARDS side by side: the dot and the sentence, the
 * chosen card in the identity colour. A select cut every option off at its width; a list
 * of radio lines made three sentences of the same weight to read down through.
 *
 * @param {{value: string|number, label: string}[]} options
 */
export function RadioList({ name, value, options, onChange, labelledBy }) {
  return (
    <div className="set-choices" role="radiogroup" aria-labelledby={labelledBy}>
      {options.map((option) => {
        const checked = String(value) === String(option.value)
        return (
          <label key={option.value} className={`set-choice${checked ? ' is-checked' : ''}`}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={checked}
              onChange={() => onChange(option.value)}
            />
            <span className="set-choice__dot" aria-hidden="true" />
            <span className="set-choice__label">
              <bdi>{option.label}</bdi>
            </span>
          </label>
        )
      })}
    </div>
  )
}

/* Gregorian with Latin digits in Arabic too — `ar` alone prints the Hijri calendar. */
const PUBLISHED_DAY = new Intl.DateTimeFormat(isEnglish ? 'en-GB' : 'ar-u-ca-gregory-nu-latn', {
  dateStyle: 'medium',
})

/**
 * What is already in place under an upload whose file has no preview — the user manual. A
 * PDF cannot be drawn the way a logo can, so without this line the card could not say
 * whether a guide had ever been published, or which one users are reading now.
 *
 * @param {{url: string, size: number, updatedAt: string|null}|null} file
 */
export function PublishedFile({ file }) {
  const U = settingsStrings.userManual

  if (!file) {
    return (
      <p className="set-published is-empty">
        <bdi>{U.notPublished}</bdi>
      </p>
    )
  }

  const facts = [
    formatFileSize(file.size),
    file.updatedAt ? PUBLISHED_DAY.format(new Date(file.updatedAt)) : null,
  ].filter(Boolean)

  return (
    <p className="set-published">
      <span className="set-published__state">
        <bdi>{U.published}</bdi>
      </span>
      <bdi className="set-published__facts">{facts.join(isEnglish ? ', ' : '، ')}</bdi>
      <a className="set-link" href={file.url} target="_blank" rel="noopener">
        <bdi>{U.view}</bdi>
      </a>
    </p>
  )
}
